import { createVault, updateVault } from '@/api';
import { useDataSourceList } from '@/hooks';
import { Vault, VaultBase } from '@/types';
import {
  Autocomplete,
  Button,
  ContentBlock,
  Grid,
  Input,
  useNotifications,
} from '@prometheus/components';
import { SecondaryContentWrapper, FormElement } from '@prometheus/features';
import { Status } from '@prometheus/types';
import { isEqual } from 'lodash';
import { FC, useState } from 'react';

export interface CreateVaultProps {
  isEdit?: boolean;
  defaultValue?: Vault;
  onClose?: () => void;
}
export const CreateVault: FC<CreateVaultProps> = ({
  isEdit,
  defaultValue,
  onClose,
}) => {
  const { enqueueSnackbar } = useNotifications();

  const { list: dataSources, status } = useDataSourceList(true);
  const [vaultStatus, setVaultStatus] = useState(Status.INITIAL);
  const [state, setState] = useState<VaultBase | Vault>(
    defaultValue || {
      name: '',
      description: '',
      dataSource: null,
      vaultSchema: '',
    }
  );
  const isLoading = status === Status.IN_PROGRESS;

  const handleVaultCreation = async (e: any) => {
    e.preventDefault();
    setVaultStatus(Status.IN_PROGRESS);
    const newValue = {
      name: state.name,
      vaultSchema: state.vaultSchema,
      dataSource: state.dataSource,
      description: state.description,
    };
    try {
      if (isEdit && defaultValue?.id) {
        await updateVault(defaultValue.id, newValue);
      } else {
        await createVault(state);
      }
      onClose();
      enqueueSnackbar({
        variant: 'success',
        message: `Vault was successfully ${isEdit ? 'updated' : 'created'}`,
      });
      setVaultStatus(Status.SUCCESS);
    } catch (err) {
      enqueueSnackbar({
        variant: 'error',
        message: 'Something went wrong...',
      });
      setVaultStatus(Status.ERROR);
    }
  };

  const isDisabled =
    vaultStatus === Status.IN_PROGRESS ||
    isEqual(state, defaultValue) ||
    !state.name ||
    !state.dataSource ||
    !state.vaultSchema;
  return (
    <Grid container>
      <SecondaryContentWrapper>
        <form onSubmit={handleVaultCreation} className="create-vault__form">
          <Grid item xs={12}>
            <ContentBlock>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormElement name="name" textLabel="Name" required>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      size="medium"
                      placeholder="Enter name..."
                      value={state.name}
                      onChange={(e: any) =>
                        setState({ ...state, name: e.target.value })
                      }
                      fullWidth
                    />
                  </FormElement>
                </Grid>
                <Grid item xs={12}>
                  <FormElement name="description" textLabel="Description">
                    <Input
                      id="description"
                      name="description"
                      placeholder="Enter description..."
                      type="text"
                      size="medium"
                      value={state.description}
                      onChange={(e: any) =>
                        setState({ ...state, description: e.target.value })
                      }
                      fullWidth
                    />
                  </FormElement>
                </Grid>
                <Grid item xs={12}>
                  <FormElement
                    name="dataSource"
                    textLabel="Data source"
                    required
                  >
                    <Autocomplete
                      className=""
                      name="dataSource"
                      id="dataSource"
                      options={dataSources || []}
                      value={state.dataSource}
                      placeholder="Select data source or start typing..."
                      onChange={(e: any, newValue: any) => {
                        setState({ ...state, dataSource: newValue });
                      }}
                      fullWidth
                      getOptionLabel={function (item: any): string {
                        return item.name || '';
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      loading={isLoading}
                    />
                  </FormElement>
                </Grid>
                <Grid item xs={12}>
                  <FormElement
                    name="vaultSchema"
                    textLabel="Vault schema"
                    required
                  >
                    <Input
                      id="vaultSchema"
                      name="vaultSchema"
                      placeholder="Enter vault schema..."
                      type="text"
                      size="medium"
                      value={state.vaultSchema}
                      onChange={(e: any) =>
                        setState({ ...state, vaultSchema: e.target.value })
                      }
                      fullWidth
                    />
                  </FormElement>
                </Grid>
              </Grid>

              <Grid item xs={12} textAlign="right" pt={2}>
                <Button
                  variant="contained"
                  color="success"
                  size="medium"
                  sx={{ mt: 2 }}
                  type="submit"
                  disabled={isDisabled}
                >
                  {isEdit ? 'Update' : 'Create'}
                </Button>
              </Grid>
            </ContentBlock>
          </Grid>
        </form>
      </SecondaryContentWrapper>
    </Grid>
  );
};
